import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  CumprimentoDetalhes,
  AtividadeRecente,
  TimelineItemData,
  Anotacao,
} from '@/types/cumprimento/cumprimentoTypes';
import {
  buscarCumprimentoPorId,
  listarAtividades,
  buscarTimeline,
  atualizarStatusCumprimento,
  arquivarCumprimento,
  listarAnotacoes,
  criarAnotacao,
  atualizarAnotacao,
  excluirAnotacao,
} from './actions';
import type { StatusCumprimento } from '@/types';

interface CumprimentoState {
  cumprimento: CumprimentoDetalhes | null;
  atividades: AtividadeRecente[];
  timeline: TimelineItemData[];
  anotacoes: Anotacao[];
  loading: boolean;
  loadingAtividades: boolean;
  loadingTimeline: boolean;
  loadingAnotacoes: boolean;
  error: string | null;
}

const initialState: CumprimentoState = {
  cumprimento: null,
  atividades: [],
  timeline: [],
  anotacoes: [],
  loading: false,
  loadingAtividades: false,
  loadingTimeline: false,
  loadingAnotacoes: false,
  error: null,
};

const cumprimentoSlice = createSlice({
  name: 'cumprimento',
  initialState,
  reducers: {
    limparCumprimento: (state) => {
      state.cumprimento = null;
      state.atividades = [];
      state.timeline = [];
      state.anotacoes = [];
      state.error = null;
    },
    limparErro: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(buscarCumprimentoPorId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        buscarCumprimentoPorId.fulfilled,
        (state, action: PayloadAction<CumprimentoDetalhes>) => {
          state.loading = false;
          state.cumprimento = action.payload;
        },
      )
      .addCase(buscarCumprimentoPorId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(listarAtividades.pending, (state) => {
        state.loadingAtividades = true;
      })
      .addCase(listarAtividades.fulfilled, (state, action: PayloadAction<AtividadeRecente[]>) => {
        state.loadingAtividades = false;
        state.atividades = action.payload;
      })
      .addCase(listarAtividades.rejected, (state, action) => {
        state.loadingAtividades = false;
        state.error = action.payload as string;
      })

      .addCase(buscarTimeline.pending, (state) => {
        state.loadingTimeline = true;
      })
      .addCase(buscarTimeline.fulfilled, (state, action: PayloadAction<TimelineItemData[]>) => {
        state.loadingTimeline = false;
        state.timeline = action.payload;
      })
      .addCase(buscarTimeline.rejected, (state, action) => {
        state.loadingTimeline = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarStatusCumprimento.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        atualizarStatusCumprimento.fulfilled,
        (state, action: PayloadAction<CumprimentoDetalhes>) => {
          state.loading = false;
          state.cumprimento = action.payload;
        },
      )
      .addCase(atualizarStatusCumprimento.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(arquivarCumprimento.pending, (state) => {
        state.loading = true;
      })
      .addCase(arquivarCumprimento.fulfilled, (state) => {
        state.loading = false;
        if (state.cumprimento) {
          state.cumprimento.status = 'arquivado' as StatusCumprimento;
        }
      })
      .addCase(arquivarCumprimento.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(listarAnotacoes.pending, (state) => {
        state.loadingAnotacoes = true;
      })
      .addCase(listarAnotacoes.fulfilled, (state, action: PayloadAction<Anotacao[]>) => {
        state.loadingAnotacoes = false;
        state.anotacoes = action.payload;
      })
      .addCase(listarAnotacoes.rejected, (state, action) => {
        state.loadingAnotacoes = false;
        state.error = action.payload as string;
      })

      .addCase(criarAnotacao.fulfilled, (state, action: PayloadAction<Anotacao>) => {
        state.anotacoes.unshift(action.payload);
      })

      .addCase(atualizarAnotacao.fulfilled, (state, action: PayloadAction<Anotacao>) => {
        const index = state.anotacoes.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.anotacoes[index] = action.payload;
        }
      })

      .addCase(excluirAnotacao.fulfilled, (state, action: PayloadAction<string>) => {
        state.anotacoes = state.anotacoes.filter((a) => a.id !== action.payload);
      });
  },
});

export default cumprimentoSlice.reducer;
